import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useSnackbar } from "notistack";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import TextField from "@mui/material/TextField";
import { useContent } from "../../hooks/useContent";
import FormButtons from "../../Components/GeneralComponents/FormButtons";
import { useNavigate, useParams } from "react-router-dom";
import {
  add_organization_api,
  get_organization_detail_api,
  update_organization_api,
} from "../../DAL/Organization";
import { useAuth } from "../../hooks/useAuth";
import PageLoader from "../../Components/GeneralComponents/PageLoader";
import {
  isPasswordStrongEnough,
  passwordPolicyMessage,
} from "../../utils/passwordPolicy";

const CreateOrUpdateOrganization = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { setPageHeading } = useContent();
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const { organization_id } = useParams();

  const [inputs, setInputs] = useState({
    partner_id: userInfo.id,
    name: "",
    website: "",
    logo: "",
    number_of_licenses: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    start_date: dayjs().format("YYYY-MM-DD"),
    end_date: dayjs().format("YYYY-MM-DD"),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const getOrganizationDetail = async () => {
    const result = await get_organization_detail_api(organization_id);
    if (result.code === 200) {
      setInputs((old) => ({ ...old, ...result.organization }));
    } else {
      enqueueSnackbar(`${result.message}`, { variant: "error" });
    }
    setIsLoadingPage(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, value) => {
    setInputs((prev) => ({
      ...prev,
      [name]: value ? value.format("YYYY-MM-DD") : "",
    }));
  };

  const showToast = (message) => enqueueSnackbar(`${message}`, { variant: "error" });

  const validate = () => {
    if (!inputs.name?.trim())
      return showToast("Organization name is required");
    if (!inputs.email?.trim()) return showToast("Admin email is required");
    if (!inputs.number_of_licenses)
      return showToast("Number of licenses is required");
    if (!inputs.first_name?.trim()) return showToast("First name is required");
    if (!inputs.last_name?.trim()) return showToast("Last name is required");
    if (!inputs.phone_number?.trim())
      return showToast("Phone number is required");
    if (!organization_id && !isPasswordStrongEnough(inputs.password)) {
      return showToast(passwordPolicyMessage);
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);

    const postObject = {
      name: inputs.name,
      website: inputs.website || "",
      logo: inputs.logo || null,
      number_of_licenses: parseInt(inputs.number_of_licenses, 10),
      email: inputs.email,
      first_name: inputs.first_name,
      last_name: inputs.last_name,
      phone_number: inputs.phone_number || "",
      start_date: inputs.start_date,
      end_date: inputs.end_date,
      plan: "basic",
    };

    if (organization_id) {
      postObject.organization_id = organization_id;
    } else {
      postObject.password = inputs.password;
    }

    const result = organization_id
      ? await update_organization_api(postObject)
      : await add_organization_api(postObject);

    if (result.code === 200) {
      enqueueSnackbar(`${result.message}`, { variant: "success" });
      navigate(`/home`);
    } else {
      enqueueSnackbar(`${result.message}`, { variant: "error" });
    }
    setIsLoading(false);
  };

  const handleCancel = () => navigate("/manage-organizations");

  useEffect(() => {
    setPageHeading(`${organization_id ? "Update" : "Create"}  Organization`);
    if (organization_id) {
      getOrganizationDetail();
    } else {
      setIsLoadingPage(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run when route id changes
  }, [organization_id]);

  if (isLoadingPage) return <PageLoader />;

  return (
    <div className="login-page container">
      <div className="d-flex justify-content-center">
        <div className="container mx-auto">
          <div className="form-wrapper">
            <form onSubmit={handleSubmit}>
              {/* <div className="d-flex justify-content-end mb-4">
                <FormButtons {...{ isLoading, handleCancel }} />
              </div> */}
              <div className="form-group mb-3">
                <label>Partner ID</label>
                <div className="input-wrapper">
                  <Icon icon="mdi:account-outline" className="input-icon" />
                  <input
                    type="text"
                    name="partner_id"
                    value={inputs.partner_id}
                    placeholder="Partner ID"
                    className="form-control"
                    readOnly
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label>
                      Organization Name{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <Icon
                        icon="mdi:office-building-outline"
                        className="input-icon"
                      />
                      <input
                        type="text"
                        name="name"
                        value={inputs.name}
                        onChange={handleChange}
                        placeholder="Organization Name"
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>
                      Number of Licenses <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <span className="input-icon">#</span>
                      <input
                        type="number"
                        name="number_of_licenses"
                        value={inputs.number_of_licenses}
                        onChange={handleChange}
                        placeholder="Number of Licenses"
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>License Start Date</label>
                    <div className="input-wrapper">
                      <Icon
                        icon="mdi:calendar"
                        className="input-icon datepicker-icon"
                      />
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          inputFormat="DD/MM/YYYY"
                          className="form-control mui-date-picker"
                          value={
                            inputs.start_date ? dayjs(inputs.start_date) : null
                          }
                          onChange={(v) => handleDateChange("start_date", v)}
                          maxDate={dayjs()}
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>License End Date</label>
                    <div className="input-wrapper">
                      <Icon
                        icon="mdi:calendar"
                        className="input-icon datepicker-icon"
                      />
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          inputFormat="DD/MM/YYYY"
                          className="form-control mui-date-picker"
                          value={
                            inputs.end_date ? dayjs(inputs.end_date) : null
                          }
                          onChange={(v) => handleDateChange("end_date", v)}
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>Organization Logo URL</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:image-outline" className="input-icon" />
                      <input
                        type="text"
                        name="logo"
                        value={inputs.logo}
                        onChange={handleChange}
                        placeholder="https://example.com/logo.png"
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>Organization Website</label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:web" className="input-icon" />
                      <input
                        type="text"
                        name="website"
                        value={inputs.website}
                        onChange={handleChange}
                        placeholder="https://www.example.com"
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <h4 className="my-3 middle-heading">Primary Contact</h4>
              <div className="row mb-3">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label>
                      Admin Email <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:email-outline" className="input-icon" />
                      <input
                        type="email"
                        name="email"
                        value={inputs.email}
                        onChange={handleChange}
                        placeholder="Admin Email"
                        className="form-control"
                        disabled={Boolean(organization_id)}
                        required={!Boolean(organization_id)}
                      />
                    </div>
                  </div>
                </div>
                {!organization_id && (
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label>
                        Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-wrapper position-relative">
                        <Icon icon="mdi:lock-outline" className="input-icon" />
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={inputs.password}
                          onChange={handleChange}
                          placeholder="Password"
                          className="form-control"
                          required
                        />
                        <Icon
                          icon={showPassword ? "mdi:eye-off" : "mdi:eye"}
                          className="eye-icon"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ cursor: "pointer", position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)" }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="col-md-6">
                  <div className="form-group">
                    <label>
                      First Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:account-outline" className="input-icon" />
                      <input
                        type="text"
                        name="first_name"
                        value={inputs.first_name}
                        onChange={handleChange}
                        placeholder="First Name"
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>
                      Last Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:account-outline" className="input-icon" />
                      <input
                        type="text"
                        name="last_name"
                        value={inputs.last_name}
                        onChange={handleChange}
                        placeholder="Last Name"
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label>
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <Icon icon="mdi:phone-outline" className="input-icon" />
                      <input
                        type="text"
                        name="phone_number"
                        value={inputs.phone_number}
                        onChange={handleChange}
                        placeholder="Phone Number"
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end mb-4">
                <FormButtons
                  {...{
                    isLoading,
                    handleCancel,
                    isRelative: true,
                  }}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrUpdateOrganization;
